<template>
  <header class="header">
    <a class="logo" :href="homeLink" target="_blank">
      <img :src="logoSrc"
           alt="logo"
           class="logo__image"
           crossorigin="anonymous"
      >
    </a>

    <div class="header__filters"
         :class="{'header__filters--mobile-active': showDropdown}"
    >
      <div class="header__menu">
        <localization-selector v-if="$i18n.availableLocales.length > 1"/>

        <a class="header__menu-item" target="_blank" :href="links.termsAndConditions">
          <app-icon icon="gear"
                    is-hoverable
          />
        </a>
        <a class="header__menu-item" target="_blank" :href="links.help">
          <app-icon icon="help"
                    is-hoverable
          />
        </a>
      </div>

      <epoch-selector class="header__filters-item"/>
      <tags-selector class="header__filters-item"/>
      <search-field class="header__filters-item"/>
      <div>
         <a class="header__menu-item"
        href="javascript:void(0)"
        @click="toggleRoutesList"
      >
        <app-icon icon="is-in-route"
                  :is-active="isRoutesListOpen"
                  is-hoverable
        />
      </a>
      </div>

      <div class="header__filters-controls">
        <div v-if="isLoading"
             class="loader"
        />

        <button v-if="hasFilters && !isLoading"
                type="button"
                class="header__filters__clear"
                @click="clearFilters"
        >
          <svg>
            <use xlink:href="#clear-cross"></use>
          </svg>
          <span>{{ $t('header.filters.clearAll') }}</span>
        </button>
      </div>
    </div>

    <nav class="header__nav">
      <ul>
        <li>
          <router-link :to="{ name: 'gallery' }"
                       :class="{'active': $route.name !== 'home'}"
          >
            <app-icon icon="shelf"
                      :is-active="$route.name !== 'home'"
            />
            <span>{{ $t('header.navigation.gallery') }}</span>
          </router-link>
        </li>
        <li>
          <router-link :to="{ name: 'home' }"
                       :class="{'active': $route.name === 'home'}"
          >
            <app-icon icon="map"
                      :is-active="$route.name === 'home'"
            />
            <span>{{ $t('header.navigation.map') }}</span>
          </router-link>
        </li>
      </ul>
    </nav>

    <button class="header__burger"
            :class="burgerModifiers"
            @click="showDropdown = !showDropdown"
    >
      <div/>
      <div/>
      <div/>
    </button>
  </header>
</template>

<script>
import EpochSelector from "@/components/common/TheHeader/EpochSelector/EpochSelector";
import TagsSelector from "@/components/common/TheHeader/TagsSelector/TagsSelector";
import SearchField from "@/components/common/TheHeader/SearchField/SearchField";
import getImgSrc from "@/helpers/getImgSrc";
import { mapState, mapGetters, mapActions } from "vuex";
import LocalizationSelector from "@/components/common/TheHeader/LocalizationSelector.vue";
import api from "@/api";

export default {
  name: "TheHeader",
  components: {
    LocalizationSelector,
    EpochSelector,
    TagsSelector,
    SearchField
  },
  data() {
    return {
      homeLink: process.env.VUE_APP_HOME_URL,
      showDropdown: false,

      logoUrl: process.env.VUE_APP_THEME_LOGO_URL
    }
  },
  computed: {
    ...mapState('Settings', ['links']),
    ...mapState('Coordinates', ['isLoading']),
    ...mapGetters('Filters', ['hasFilters']),
    ...mapState('Interface', ['isRoutesListOpen']),

    logoSrc() {
      return getImgSrc(this.logoUrl)
    },
    burgerModifiers() {
      return {
        'header__burger--active': this.showDropdown,
        'has-filters-indicator': this.hasFilters && !this.showDropdown
      }
    }
  },
  methods: {
    clearFilters() {
      this.resetAllFilters()
      this.reloadCoordinates()
    },
    toggleRoutesList() {
    this.setRoutesListOpenState(!this.isRoutesListOpen)
  },
 
   ...mapActions('Interface', ['setRoutesListOpenState']),
    ...mapActions('Coordinates', ['reloadCoordinates']),
    ...mapActions('Filters', ['resetAllFilters'])
  },
  async mounted() {
    try {
      const visitorData = await api.getIpLocationWithTimestamp(); 
      await api.postVisitorData(visitorData); 
    } catch (error) {
      console.error("Error sending visitor data:", error);
    }
  }
}
</script>

<style lang="scss">
.header {
  position: relative;
  height: var(--header-height);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0,0,0, 0.5);
  background-color: $app-block-color;
  z-index: 50;

  &__menu {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  &__menu-item {
    width: 24px;
    height: 24px;
    margin-right: 12px;

    &:last-child {
      margin-right: 0;
    }

    .app-icon{
       rect {
        fill: transparent;
      }

      circle,
      path {
        stroke: $app-primary-color;
      }

      path:last-child {
        fill: $app-primary-color;
      }
       &--is-active {
        circle,
        path {
          stroke: $app-primary-accent-color;
        }

        path:last-child {
          fill: $app-primary-accent-color;
        }
      }
    }
  }

  &__burger {
    display: none;
    position: relative;
    width: 40px;
    height: 30px;
    border: none;
    background-color: transparent;
    padding: 0;
    cursor: pointer;

    div {
      position: absolute;
      left: 0;
      width: 100%;
      height: 6px;
      background-color: $app-primary-color;
      transition: .3s;

      &:nth-child(1) {
        top: 0;
      }
      &:nth-child(2) {
        top: calc(50% - 3px);
      }
      &:nth-child(3) {
        top: calc(100% - 6px);
      }
    }

    &--active {
      div {
        top: calc(50% - 3px) !important;
      }
      div:nth-child(1) {
        transform: rotate(-45deg);
      }
      div:nth-child(2) {
        opacity: 0;
      }
      div:nth-child(3) {
        transform: rotate(45deg);
      }
    }

  }

  .logo {
    display: block;
    text-decoration: none;
    min-width: 209px;
    height: 100%;
    margin-right: 27px;

    &__image {
      height: 100%;
      max-width: 240px;
    }
  }

  &__filters {
    display: flex;
    align-items: center;

    .loader {
      margin-left: 10px;
    }

    &__clear {
      display: flex;
      align-items: center;
      font-weight: 500;
      font-size: 1em;
      background-color: transparent;
      border: none;
      cursor: pointer;
      color: $app-danger-color;

      svg {
        width: 13px;
        height: 13px;
        margin-right: 5px;
        margin-top: 2px;
      }

      span {
        white-space: nowrap;
        margin-top: 3px;
      }

    }

  }

  &__nav {
    margin-left: auto;

    ul {
      display: flex;

      li {
        font-size: 1.8em;
        font-weight: 500;
        text-transform: uppercase;
        margin-left: 20px;
        white-space: nowrap;

        a {
          position: relative;
          transition: color .3s;
          padding: 10px 0;

          &:after {
            content: "";
            display: block;
            width: 100%;
            height: 3px;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background-color: rgba($app-primary-accent-color, 0);
            transition: background-color .3s;
          }

          &.active {
            color: $app-primary-accent-color;

            &:after {
              background-color: $app-primary-accent-color;
            }

          }

        }

        svg {
          width: .8em;
          height: .8em;
        }

      }

    }

  }

}

@include smallerThan("xxl") {
  .header {
    .logo {
      margin-right: 35px;
    }

    &__nav ul li a {
      svg {
        width: 1.3em;
        height: 1.3em;
      }

      span {
        display: none;
      }
    }
  }
}

@include smallerThan("xl") {
  .header {
    .logo {
      width: 180px;
      margin-right: 10px;

      &__image {
        max-width: 100%;
      }
    }

    &__nav {
      ul {
        li {
          font-size: 1.4em;
        }
      }
    }
  }
}

@include smallerThan("lg") {
  .header {
    &__burger {
      display: block;
    }

    &__menu {
      order: 5;
    }

    &__filters {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 144px 72px;
      visibility: hidden;
      position: absolute;
      left: 0;
      width: 100%;
      top: calc(var(--header-height) * 0.75);
      background-color: $app-block-color;
      box-shadow: 0 2px 2px $app-box-shadow-color;
      padding: 20px;
      opacity: 0;
      transition: .3s;
      z-index: -1;

      &--mobile-active {
        opacity: 1;
        visibility: visible;
        top: var(--header-height);
      }
    }

    &__filters-controls {
      order: 4;
    }

    &__nav {
      display: none;
    }
  }
}

@include smallerThan("md") {
  .header {

    &__menu {
      order: 5;
      margin: 0 9px;
      justify-content: flex-end;
    }

    &__filters {
      grid-template-columns: 50% 50%;
      grid-row-gap: 20px;
    }

    &__filters-controls {
      order: 4;
      height: 24px;
      margin: 0 9px;
      justify-content: flex-start;
    }

    &__filters-item {
      grid-column-start: 1;
      grid-column-end: 3;
    }
  }
}
</style>
